import { EScreenType, IProcessStepScreensInfo, IScreenDesignInfo, IWFStep, IWorkFlow } from "@kuika/kuika-cl-model";
import React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { EScreenRole, resolveScreenRole } from "../../../shared/utility/screen-role-helper";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import "./step-screen-form-wizard-assign-screen-dropdown.scss";

export interface IAssignScreenDropdownProps {
  value: string | undefined;
  onChange: (screenId: string | null) => void;
  currentProcessId: string | null;
  currentStepId: string | null;
  pendingAssignments?: IPendingUnmanagedAssignment[];
  size?: "sm" | "md" | "lg";
}

export interface IPendingUnmanagedAssignment {
  processId: string;
  stepId: string;
  screenId: string;
}

interface IScreenLinkMeta {
  processName: string | undefined;
  stepName: string | undefined;
  processId: string;
  stepId: string;
}

interface IScreenOption {
  value: string;
  label: React.ReactNode;
  searchLabel: string;
  disabled: boolean;
}

class AssignScreenDropdown extends React.PureComponent<IAssignScreenDropdownProps & IWithSDProps> {
  getLinkMeta = (screenId: string): IScreenLinkMeta | undefined => {
    const { project, workFlowList, findStepRecursively } = this.props.sd;
    const link: IProcessStepScreensInfo | undefined = project?.processStepScreenIds
      ?.filter((p) => p.screenId === screenId)
      ?.at(-1);

    if (!link) return undefined;

    const process: IWorkFlow | undefined = workFlowList?.find((wf: IWorkFlow) => wf.id === link.processId);
    const step: IWFStep | undefined = process ? findStepRecursively?.(process, (s) => s.id === link.stepId) : undefined;

    return {
      processName: process?.displayName ?? process?.name,
      stepName: step?.name ?? undefined,
      processId: link.processId,
      stepId: link.stepId
    };
  };

  getAssignableScreens = (): IScreenDesignInfo[] => {
    const { screenList, project } = this.props.sd;

    // No screen list, or no loaded project → nothing assignable. Guarding project here
    // keeps the shared resolveScreenRole a pure, guard-free extraction of the original.
    if (!screenList || !project) return [];

    // Assignable = a normal-typed screen whose role is unassigned (none) or a process-step
    // screen (the latter still shown, with an "In use"/"Pending" badge). Every reserved
    // system role (Starting/Signin/Offline/Document Upload Link/…) is excluded via the
    // shared resolveScreenRole, so a newly added role is filtered here automatically — no
    // per-role slot list to keep in sync.
    return screenList.filter((screen: IScreenDesignInfo) => {
      const screenType = screen.publicMetaData?.screenType;
      const isNormalType =
        screenType === undefined || screenType === EScreenType.Default || screenType === EScreenType.WebScreen;
      const role = resolveScreenRole(screen.id, project);
      const isAssignableRole = role === EScreenRole.none || role === EScreenRole.processStepScreen;
      return isNormalType && isAssignableRole;
    });
  };

  isLinkedElsewhere = (meta: IScreenLinkMeta | undefined): boolean => {
    if (!meta) return false;
    return meta.processId !== this.props.currentProcessId || meta.stepId !== this.props.currentStepId;
  };

  getPendingForScreenFromOtherStep = (screenId: string): IPendingUnmanagedAssignment | undefined => {
    const { pendingAssignments, currentProcessId, currentStepId } = this.props;
    if (!pendingAssignments) return undefined;
    return pendingAssignments.find(
      (p) => p.screenId === screenId && !(p.processId === currentProcessId && p.stepId === currentStepId)
    );
  };

  getStepNameById = (processId: string, stepId: string): string | undefined => {
    const { workFlowList, findStepRecursively } = this.props.sd;
    const process: IWorkFlow | undefined = workFlowList?.find((wf: IWorkFlow) => wf.id === processId);
    if (!process) return undefined;
    const step = findStepRecursively?.(process, (s) => s.id === stepId);
    return step?.name;
  };

  getProcessNameById = (processId: string): string | undefined => {
    const process: IWorkFlow | undefined = this.props.sd.workFlowList?.find((wf: IWorkFlow) => wf.id === processId);
    return process?.displayName ?? process?.name;
  };

  buildOptions = (screens: IScreenDesignInfo[]): IScreenOption[] => {
    return screens
      .map((screen) => {
        const meta = this.getLinkMeta(screen.id);
        const linkedElsewhere = this.isLinkedElsewhere(meta);
        const pendingElsewhere = this.getPendingForScreenFromOtherStep(screen.id);

        let path: string | undefined;
        let badgeText: string | undefined;
        if (linkedElsewhere && meta) {
          path = `${meta.processName ?? meta.processId} › ${meta.stepName ?? meta.stepId}`;
          badgeText = "In use";
        } else if (pendingElsewhere) {
          const processName = this.getProcessNameById(pendingElsewhere.processId) ?? pendingElsewhere.processId;
          const stepName =
            this.getStepNameById(pendingElsewhere.processId, pendingElsewhere.stepId) ?? pendingElsewhere.stepId;
          path = `${processName} › ${stepName}`;
          badgeText = "Pending";
        }

        return {
          value: screen.id,
          searchLabel: screen.name,
          disabled: linkedElsewhere || !!pendingElsewhere,
          label: (
            <div className="kuika-assign-screen-option kuika-overflow-text-host">
              <span className="kuika-assign-screen-option__name" title={screen.name}>
                <KuikaOverflowText>{screen.name}</KuikaOverflowText>
              </span>
              {badgeText && path && (
                <span className="kuika-assign-screen-option__meta" title={`${badgeText}: ${path}`}>
                  <span className="kuika-assign-screen-option__badge">{badgeText}</span>
                  <span className="kuika-assign-screen-option__path">{path}</span>
                </span>
              )}
            </div>
          )
        };
      })
      .sort((a, b) => {
        if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
        return a.searchLabel.localeCompare(b.searchLabel);
      });
  };

  handleChange = (value: string | number | Array<string | number> | null) => {
    if (typeof value === "string") {
      this.props.onChange(value);
    } else {
      this.props.onChange(null);
    }
  };

  render() {
    const assignable = this.getAssignableScreens();
    const options = this.buildOptions(assignable);
    const isEmpty = options.length === 0;

    return (
      <KuikaSelectbox
        searchable
        allowClear
        size={this.props.size ?? "md"}
        placeholder={isEmpty ? "No assignable screens" : "Assign existing screen..."}
        disabled={isEmpty}
        value={this.props.value}
        onChange={this.handleChange}
        onClear={() => this.props.onChange(null)}
        options={options}
        overlayClassName="kuika-assign-screen-dropdown"
      />
    );
  }
}

const assignScreenDropdown = withSD<IAssignScreenDropdownProps>(AssignScreenDropdown);
export { assignScreenDropdown as AssignScreenDropdown };
